<template>
  <v-card class="ma-0 pa-2 card" color="background" variant="elevated" :elevation="isOverlay ? 0 : 4" density="compact">
    <v-form class="ma-0 pa-0" @submit.prevent density="compact" autocomplete="off">
      <input id="username" autocomplete="username" type="text" style="display:none">
      <input id="password" autocomplete="new-password" type="password" style="display:none">
      <v-card-item v-if="!show" class="provider-summary" @click="show = true">
        <template #prepend>
          <div class="mr-3 provider-summary-avatar">
            <img
              v-if="showStandaloneIcon && iconMode !== 'mono'"
              :src="(icon as ProviderIcon).src"
              :alt="`${displayName} icon`"
              class="provider-summary-avatar-image"
              @error="onIconError"
            >
            <span
              v-else-if="showStandaloneIcon"
              class="provider-summary-avatar-mask"
              :style="iconMaskStyle"
              role="img"
              :aria-label="`${displayName} icon`"
            ></span>
            <span v-else class="provider-summary-initials" :style="{ backgroundColor: brandColor }">{{ providerInitials }}</span>
          </div>
        </template>
        <v-card-title class="d-flex align-center">
          <span class="text-truncate">{{ displayName }}</span>
          <v-chip v-if="hasLLM" size="x-small" class="ml-2">LLM</v-chip>
          <v-chip v-if="hasAudio" size="x-small" class="ml-2">Voice</v-chip>
        </v-card-title>
        <v-card-subtitle class="text-truncate">
          {{ providerSubtitle }}
        </v-card-subtitle>
      </v-card-item>
      <v-card-text v-if="!show" class="pt-0 pb-0 provider-summary-text">
        <div class="d-flex align-center">
          <div class="d-flex flex-wrap align-center flex-grow-1">
            <v-chip size="small" variant="tonal" class="mr-2">{{ llmModels.length }} models</v-chip>
            <v-chip size="small" variant="tonal" class="mr-2">{{ voiceModels.length }} voices</v-chip>
          </div>
          <v-btn v-if="!isOverlay" color="primary" icon="mdi-chevron-down" size="large" variant="plain"
            class="provider-summary-expand" @click.stop="show = true" />
        </div>
      </v-card-text>
      <v-expand-transition>
        <v-list class="ma-0 pa-0 card-content" v-show="show" density="compact">
          <!-- Provider Key/Name -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Select a built-in provider or enter a unique name for a custom provider.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-combobox v-model="providerKeyValue" @update:modelValue="providerKeyUpdated" :items="builtInProviders"
              label="Provider Name" class="pt-2" variant="outlined" density="compact"
              :rules="[() => !!providerKeyValue || 'Provider Name is required']">
              <template v-slot:append-inner>
                <v-tooltip text="Custom Provider" v-if="isCustomProvider">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="primary" icon="mdi-pencil-outline" />
                  </template>
                </v-tooltip>
              </template>
            </v-combobox>
          </v-list-item>

          <!-- URL (Conditional) -->
          <v-list-item v-if="isCustomProvider" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="URL is required for custom providers (e.g., http://localhost:11434).">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="url" @blur="checkChanges" label="Provider URL" class="pt-2" variant="outlined"
              density="compact" :rules="[() => !isCustomProvider || !!url || 'URL is required for custom providers']">
            </v-text-field>
          </v-list-item>

          <!-- API Key (Conditional Optionality) -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" :text="apiKeyTooltipText">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <SecretInput v-model="apiKey" @blur="checkChanges" :label="apiKeyLabel" class="pt-2"
              :rules="[() => isCustomProvider || !!apiKey || 'API Key is required for built-in providers']" />
          </v-list-item>

          <!-- Provider Capabilities -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Select provider capabilities. When omitted, API defaults to ['llm'].">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <div class="pt-2">
              <div class="text-caption mb-1">Provider Capabilities</div>
              <v-checkbox v-model="hasLLM" label="LLM" density="compact" hide-details class="display-inline mr-4" />
              <v-checkbox v-model="hasAudio" label="Audio" density="compact" hide-details class="display-inline" />
            </div>
          </v-list-item>

          <!-- Supported Models / Voices -->
          <v-list-item v-if="llmModels.length + voiceModels.length > 0"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Inventory discovered for this provider. Models and voices are grouped below.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-expansion-panels class="pt-2 w-100">
              <v-expansion-panel v-if="llmModels.length > 0" :title="`Supported LLM Models (${llmModels.length})`"
                density="compact" color="background" class="card-content">
                <v-expansion-panel-text class="card-content">
                  <v-list density="compact" class="card-content">
                    <v-list-item v-for="item in llmModels" :key="(item as any).id" density="compact">
                      <v-list-item-title>{{ item.id }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
              <v-expansion-panel v-if="voiceModels.length > 0" :title="`Supported Voices (${voiceModels.length})`"
                density="compact" color="background">
                <v-expansion-panel-text>
                  <v-list density="compact">
                    <v-list-item v-for="item in voiceModels" :key="item.id" density="compact">
                      <v-list-item-title>
                        <span>{{ item.name ?? item.id }}</span>
                        <v-chip v-if="item.labels && item.labels.descriptive" size="x-small" class="ml-2"
                          color="info">{{ item.labels.descriptive }}</v-chip>
                        <v-chip v-if="item.labels && item.labels.accent" size="x-small" class="ml-2"
                          :color="accentChipColor(item.labels.accent)">{{ item.labels.accent }}</v-chip>
                        <v-chip v-if="item.labels && item.labels.gender" size="x-small" class="ml-2"
                          :color="genderChipColor(item.labels.gender)">{{ item.labels.gender }}</v-chip>
                      </v-list-item-title>
                      <AudioPlayer v-if="item.previewUrl" :key="`${audioColor}:${item.previewUrl}`"
                        :src="item.previewUrl" :canv-height="40" :canv-width="300" :bar-color="audioColor"
                        :show-controls="true" />
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-list-item>
        </v-list>
      </v-expand-transition>

      <v-card-actions v-if="show" density="compact">
        <Button color="primary" class="mr-4" :disabled="saveDisabled" :click="updateProvider"
          icon="mdi-check-bold" size="x-large" tooltip="Save Provider" density="compact" />
        <Button color="primary" class="mr-4" :click="initiateDeleteProvider" icon="mdi-trash-can-outline"
          size="x-large" tooltip="Delete Provider" density="compact" />
        <v-spacer />
        <v-btn v-if="!isOverlay" color="primary" class="mx-auto" icon="mdi-chevron-up"
          @click="show = false" density="compact"></v-btn>
      </v-card-actions>
    </v-form>

    <div v-if="waiting" class="card-action-overlay">
      <h2>{{ actionMessage }}</h2>
    </div>

    <ConfirmationDialog v-model="showDeleteConfirmationDialog" title="Confirm Provider Deletion"
      message="Are you sure you want to delete this provider?" :itemName="providerToDeleteName" itemType="provider"
      :loading="waiting" @confirm="confirmProviderDelete" @cancel="cancelDeleteProvider" />
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import Button from '@/components/sub/Button.vue'
import SecretInput from '@/components/sub/SecretInput.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue' // Import ConfirmationDialog
import AudioPlayer from '@/components/sub/AudioPlayer.vue'
import { ProviderInventoryItem, ProviderLLM, ProviderVoice } from '@/types/msq'
import { ProviderKey, ProviderConfig, ProviderCapability } from '@/types/providers'
import vuetify from '@/main'
import { Ref } from 'vue'
import { ProviderIcon, ThemeName, getProviderBrandColor, getProviderIcon } from '@/utils/providerIcons'
// import { InternalThemeDefinition } from 'vuetify/lib/composables' // not exposed externally
import { Colors } from 'vuetify/lib/composables/theme'

interface InternalThemeDefinition {
  dark: boolean;
  colors: Colors;
  variables: Record<string, string | number>;
}

@Options({
  props: {
    providerKey: String, // This is the key used to identify an existing provider
    isOverlay: Boolean,
    startExpanded: Boolean
  },
  components: {
    Button,
    SecretInput,
    ConfirmationDialog, // Register ConfirmationDialog
    AudioPlayer
  },
  computed: {
    ...mapGetters(['providerConfigs', 'builtInProviders', 'allProviderModels', 'showGlobalTooltips', 'currentTheme']),
    theme() {
      return vuetify.theme.current
    }
  },
  watch: {
    theme: {
      handler(t: Ref<InternalThemeDefinition>) {
        this.theme_ = t
      },
      deep: true
    }
  },
  emits: ['remove-card', 'saved']
})
export default class ProviderCard extends Vue {
  private show_: boolean = true
  private providerKeyValue_: ProviderKey = '' // Bound to combobox, holds current key/name
  private url_: string = ''
  private apiKey_: string = ''
  private isNewProvider_ = true // Flag to track if it's a new provider card

  public theme!: InternalThemeDefinition
  private theme_: InternalThemeDefinition | null = null
  
  public providerConfigs!: Record<string, ProviderConfig>
  public builtInProviders!: string[]
  public allProviderModels!: Record<string, ProviderInventoryItem[]>
  public showGlobalTooltips!: boolean
  public currentTheme!: string
  public providerKey!: string 
  public isOverlay!: boolean
  public startExpanded!: boolean
  
  public waiting = false
  public actionMessage = ''
  public saveDisabled = true
  public iconLoadFailed = false

  // For delete confirmation
  public showDeleteConfirmationDialog = false
  public providerToDeleteName: string | null = null

  // Capabilities
  private capabilities_: ProviderCapability[] = ['llm']

  get capabilities(): ProviderCapability[] {
    return this.capabilities_
  }
  set capabilities(value: ProviderCapability[]) {
    this.capabilities_ = value
    this.checkChanges()
  }

  get hasLLM(): boolean {
    return this.capabilities.includes('llm')
  }
  set hasLLM(v: boolean) {
    this.toggleCapability('llm', v)
  }

  get hasAudio(): boolean {
    return this.capabilities.includes('audio')
  }
  set hasAudio(v: boolean) {
    this.toggleCapability('audio', v)
  }

  get audioColor(): string {
    const color = this.theme_ != null ? this.theme_.colors.primary : '#6200EE'
    return color
  }

  mounted() {
    this.theme_ = this.theme
  }

  toggleCapability(kind: ProviderCapability, enabled: boolean) {
    const set = new Set(this.capabilities)
    if (enabled) set.add(kind)
    else set.delete(kind)
    this.capabilities = Array.from(set)
  }

  // Chip color helpers
  genderChipColor(g?: string): string | undefined {
    if (!g) return undefined
    const v = g.toLowerCase()
    if (v === 'male') return 'primary'
    if (v === 'female') return 'secondary'
    return undefined
  }

  accentChipColor(a?: string): string | undefined {
    if (!a) return undefined
    const v = a.toLowerCase()
    if (v === 'american') return 'primary'
    if (v === 'australian') return 'secondary'
    if (v === 'british') return 'warning'
    return undefined
  }
  
  get providerKeyValue(): ProviderKey {
    return this.providerKeyValue_
  }
  
  set providerKeyValue(value: ProviderKey) {
    this.providerKeyValue_ = value
    this.checkChanges()
  }
  
  get url(): string {
    return this.url_
  }
  
  set url(value: string) {
    this.url_ = value
    this.checkChanges()
  }
  
  get apiKey(): string {
    return this.apiKey_
  }
  
  set apiKey(value: string) {
    this.apiKey_ = value
    this.checkChanges()
  }
  
  get isNewProvider(): boolean {
    return this.isNewProvider_
  }
  
  set isNewProvider(value: boolean) {
    this.isNewProvider_ = value
  }
  
  get isCustomProvider() {
    return !!this.providerKeyValue && !this.builtInProviders.includes(this.providerKeyValue as string)
  }
  
  get inventory(): ProviderInventoryItem[] {
    if (!this.providerKeyValue) return []
    return this.allProviderModels[this.providerKeyValue] || []
  }

  get llmModels(): ProviderLLM[] {
    const list = this.inventory.filter((i): i is ProviderLLM => i.object === 'model')
    return list.sort((a: ProviderLLM, b: ProviderLLM) => a.id.localeCompare(b.id))
  }

  get voiceModels(): ProviderVoice[] {
    const list = this.inventory.filter((i): i is ProviderVoice => i.object === 'voice')
    return list.sort((a: ProviderVoice, b: ProviderVoice) => (a.name ?? a.id).localeCompare(b.name ?? b.id))
  }
  
  get displayName() {
    return this.providerKeyValue || 'New Provider'
  }

  get providerInitials(): string {
    const raw = this.displayName.replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'AI'
  }

  get brandColor(): string {
    return getProviderBrandColor(this.displayName)
  }

  get icon(): ProviderIcon | null {
    return getProviderIcon(this.displayName, this.currentTheme as ThemeName)
  }

  get iconMode(): ProviderIcon['mode'] | null {
    return this.icon?.mode || null
  }

  get iconMaskStyle(): Record<string, string> {
    const icon = this.icon
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || 'currentColor'
    }
  }

  get showStandaloneIcon(): boolean {
    return !!this.icon && this.iconLoadFailed !== true
  }

  get providerSubtitle(): string {
    return this.isCustomProvider ? 'Custom provider' : 'Built-in provider'
  }

  get inventoryPreview(): string[] {
    const modelIds = this.llmModels.slice(0, 2).map((item) => item.id)
    const voiceNames = this.voiceModels.slice(0, 1).map((item) => item.name ?? item.id)
    return [...modelIds, ...voiceNames]
  }
  
  get apiKeyLabel() {
    return this.isCustomProvider ? 'API Key (Optional)' : 'API Key'
  }
  
  get apiKeyTooltipText() {
    return this.isCustomProvider
      ? 'API Key for authentication (optional for custom providers).'
      : 'API Key for authentication (required for built-in providers).'
  }
  
  get show(): boolean {
    return this.show_
  }
  
  set show(value: boolean) {
    this.show_ = value
  }
  
  get existingConfig(): ProviderConfig | null {
    if (!this.providerKey || !this.providerConfigs) return null
    return this.providerConfigs[this.providerKey] || null
  }

  created() {
    this.initializeData()
  }
  
  initializeData() {
    this.isNewProvider_ = true
    this.show_ = !this.providerKey || !!this.startExpanded
    this.saveDisabled = true
    
    if (this.providerKey && this.providerConfigs) {
      const config = this.providerConfigs[this.providerKey]
      if (config) {
        this.providerKeyValue_ = config.providerKey
        this.url_ = config.url || ''
        this.apiKey_ = config.apiKey || ''
        this.capabilities_ = (config.capabilities && config.capabilities.length > 0) ? config.capabilities : ['llm']
        this.isNewProvider_ = false
        this.show_ = !!this.startExpanded
        this.checkChanges() 
      } else {
        this.providerKeyValue_ = ''
        this.url_ = ''
        this.apiKey_ = ''
        this.capabilities_ = ['llm']
        this.isNewProvider_ = true
        this.show_ = true
      }
    } else {
      this.providerKeyValue_ = ''
      this.url_ = ''
      this.apiKey_ = ''
      this.capabilities_ = ['llm']
      this.isNewProvider_ = true
      this.show_ = true
    }
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }

  providerKeyUpdated(newKey: ProviderKey | null) {
    this.providerKeyValue = newKey || ''
    if (!this.isCustomProvider) {
      this.url = ''
    }
  }
  
  checkChanges() {
    let isValid = !!this.providerKeyValue
    if (this.isCustomProvider) {
      isValid = isValid && !!this.url
    } else {
      isValid = isValid && !!this.apiKey
    }
    // At least one capability must be selected
    isValid = isValid && this.capabilities.length > 0

    let hasChanged = this.isNewProvider 
    if (!this.isNewProvider && this.existingConfig) {
      const existingCaps = (this.existingConfig.capabilities && this.existingConfig.capabilities.length > 0)
        ? this.existingConfig.capabilities
        : ['llm']
      const capsA = Array.from(new Set(this.capabilities)).sort().join(',')
      const capsB = Array.from(new Set(existingCaps)).sort().join(',')
      hasChanged = (
        this.providerKeyValue !== this.existingConfig.providerKey ||
        this.url !== (this.existingConfig.url || '') ||
        this.apiKey !== (this.existingConfig.apiKey || '') ||
        capsA !== capsB
      )
    }
    
    this.saveDisabled = !isValid || !hasChanged
  }

  async updateProvider() {
    if (this.providerKeyValue && !this.saveDisabled) {
      this.waiting = true
      this.actionMessage = 'Saving...'
      
      const config: ProviderConfig = {
        providerKey: this.providerKeyValue,
        url: this.isCustomProvider && this.url ? this.url : undefined,
        apiKey: this.apiKey ? this.apiKey : undefined,
        capabilities: this.capabilities
      }
      
      const success = await this.$store.dispatch('addProvider', config)
      
      this.waiting = false
      this.actionMessage = ''
      
      if (success) {
        this.isNewProvider = false 
        
        await this.$store.dispatch('fetchProviderModels', { 
          providerConfig: { providerKey: this.providerKeyValue } 
        })
        
        this.checkChanges() 
        this.$emit('saved', { providerKey: this.providerKeyValue })
      }
    }
  }
  
  initiateDeleteProvider() {
    if (this.isNewProvider) {
      this.$emit('remove-card')
    } else if (this.providerKeyValue) {
      this.providerToDeleteName = this.providerKeyValue
      this.showDeleteConfirmationDialog = true
    }
  }

  async confirmProviderDelete() {
    if (this.providerToDeleteName) {
      this.waiting = true
      this.actionMessage = 'Deleting...'
      
      const success = await this.$store.dispatch('deleteProvider', this.providerToDeleteName)
      
      if (success) {
        await this.$store.dispatch('fetchProviders')
        this.$emit('remove-card')
      }
      
      this.waiting = false
      this.actionMessage = ''
    }
    this.cancelDeleteProvider()
  }

  cancelDeleteProvider() {
    this.showDeleteConfirmationDialog = false
    this.providerToDeleteName = null
  }
}
</script>

<style scoped lang="scss">
.card {
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.provider-summary {
  cursor: pointer;
}
.provider-summary-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}
.provider-summary-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.provider-summary-avatar-mask {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}
.provider-summary-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}
.provider-summary-text {
  padding-left: 72px;
}
.provider-summary-expand {
  flex-shrink: 0;
  margin-left: 8px;
}
.card-action-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  border-radius: inherit; 
}
.max-85 {
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.global-tooltip-icon {
  color: rgba(var(--v-theme-on-surface), 0.6); 
}

.display-inline {
  display: inline-block;
}
.card-content {
  background-color: inherit;
}
</style>
